import { useState } from 'react';
import { LinkButton, Flex } from '@/ui';
import { Text } from '@/ui/typography';
import { Title } from '@/ui/typography';
import { useTranslation } from 'react-i18next';
import { investorApi } from '@/api';
import { capitalizeFirstLetter } from '@/utils/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { investorActions } from '@/store/investor';
import { EMAIL_PHONE_LABELS, EMAIL_REGEX } from '@/utils/static';
import { PhoneInput } from '@/modules/kycModule/Fields';
import { FormInput } from '@/modules/kycModule/components';
import { useForm } from 'react-hook-form';
import { Modal, Button } from 'antd';
import { usePopups } from '@/hooks';

const ChangeEmailOrPhone = ({ label, setTriggerSendCode }) => {
  const { account } = useSelector((state) => state.investor);
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = usePopups();
  const dispatch = useDispatch();
  const { control, handleSubmit, reset } = useForm({ mode: 'onChange' });
  const [isOpen, setIsOpen] = useState(false);
  const [isDisabledButton, setIsDisabledButton] = useState(false);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const checkValidation = (email, phone) => {
    return new Promise((resolve, reject) => {
      investorApi
        .phoneEmailValidation(
          label === 'phone' ? phone : null,
          label === 'email' ? email.toLowerCase() : null,
          account
        )
        .then((res) => {
          if (res.status === 200) {
            const { data } = res;

            if (
              (label === 'email' && !data.validEmail) ||
              (label === 'phone' && !data.validPhone)
            ) {
              reject('UNIQUE_FAILED');
            }

            resolve();
          } else {
            reject();
          }
        })
        .catch(() => {
          reject();
        });
    });
  };

  const handleChange = async (data) => {
    setIsDisabledButton(true);

    const { email, phone } = data;
    try {
      await checkValidation(email, phone ? phone.replace('/', '') : null);

      investorApi
        .changeEmailOrCode({
          ...(label === 'email' && { newEmail: email }),
          ...(label === 'phone' && { newPhone: phone ? phone.replace('/', '') : null })
        })
        .then((resChange) => {
          setTriggerSendCode();
          if (label === 'email')
            dispatch(investorActions.setInvestor({ email: resChange.data.email }));
          if (label === 'phone')
            dispatch(investorActions.setInvestor({ phone: resChange.data.phone }));
          showSuccessToast(`${capitalizeFirstLetter(EMAIL_PHONE_LABELS[label])} has been changed`);
          setIsDisabledButton(false);
          closeModal();
          reset();
        })
        .catch((e) => {
          console.error(e);
          showErrorToast(e, true);
          setIsDisabledButton(false);
          closeModal();
          reset();
        });
    } catch (e) {
      console.error(e);
      if (e === 'UNIQUE_FAILED') {
        showErrorToast(`${capitalizeFirstLetter(EMAIL_PHONE_LABELS[label])} is already taken`);
      } else {
        showErrorToast(e, true);
        setIsDisabledButton(false);
        closeModal();
        reset();
      }
    }
  };

  return (
    <>
      <Text>
        (<LinkButton onClick={openModal}>{t('Change')}</LinkButton>)
      </Text>
      <Modal
        open={isOpen}
        onCancel={closeModal}
        footer={[
          <Button key='back' onClick={closeModal}>
            {t('Cancel')}
          </Button>,
          <Button
            key='submit'
            type='primary'
            onClick={handleSubmit(handleChange)}
            disabled={isDisabledButton}
          >
            {t('Change')}
          </Button>
        ]}
      >
        <Title level={5} $marginBottom={8}>{t(`Change ${EMAIL_PHONE_LABELS[label]}`)}</Title>
        <Flex $wide $marginTop={15} wrap>
          {label === 'email' && (
            <FormInput
              label={t('E-mail')}
              form={{
                control,
                name: 'email',
                rules: {
                  required: t('Required'),
                  validate: (v) => EMAIL_REGEX.test(v.toLowerCase()) || t('Invalid e-mail address')
                }
              }}
              textTransform={'lowercase'}
              marginBottom={24}
              required
              wide
              layout='vertical'
            />
          )}

          {label === 'phone' && (
            <PhoneInput
              form={{ control, name: 'phone', rules: { required: t('Required') } }}
              marginBottom={24}
              required
              wide
              layout='vertical'
            />
          )}
        </Flex>

        <Flex $wide $marginTop={16} gap={10} justify='flex-end'></Flex>
      </Modal>
    </>
  );
};

export default ChangeEmailOrPhone;
